<template>
  <div class="voyage-compare-map">
    <div ref="map" class="voyage-compare-map-canvas"></div>
    <div class="voyage-compare-map-legend">
      <div v-for="item in legendItems" :key="item.side" class="voyage-compare-map-legend-row">
        <span class="voyage-compare-map-legend-line" :style="{ background: item.color }"></span>
        <span class="voyage-compare-map-legend-text">{{ item.side.toUpperCase() }} · {{ item.label }}</span>
        <span v-if="item.note" class="voyage-compare-map-legend-note">{{ item.note }}</span>
      </div>
    </div>
    <div v-if="isLoading" class="voyage-compare-map-overlay">
      <wave-loader size="sm">Loading tracks...</wave-loader>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import SummarySvc from '@/services/SummarySvc';
import { apiDatetimeToUnix } from '@/util/apiDatetime';
import { parseTrailResponse } from '@/util/trail/trailData';
import { routeMarkerPoints, escapeHtml } from '@/util/trail/voyageMapExtras';
import { subscribe, HOVER } from '@/util/chart/chartSync';
import { SIDE_COLORS, nearestIndexByElapsed } from '@/views/voyage/utils/voyageCompareSeries';

const SIDE_KEYS = ['a', 'b'];
const STATUS_NOTE = { empty: '(no GPS data)', failed: '(failed)' };

function ensureStylesheet(href) {
  return new Promise((resolve) => {
    let link = document.querySelector(`link[href="${href}"]`);
    if (link && link.sheet) {
      resolve();
      return;
    }
    if (!link) {
      link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      document.head.appendChild(link);
    }
    link.addEventListener('load', () => resolve(), { once: true });
    link.addEventListener('error', () => resolve(), { once: true });
  });
}

export default {
  name: 'VoyageCompareMap',
  props: {
    // { a: master voyage, b: master voyage }
    voyages: {
      type: Object,
      required: true,
    },
    syncGroup: {
      type: String,
      default: 'voyage-compare',
    },
  },
  data() {
    return {
      tracks: {
        a: { loading: false, status: '' },
        b: { loading: false, status: '' },
      },
    };
  },
  computed: {
    voyageKey() {
      return SIDE_KEYS.map((side) => this.voyages[side].mvygId).join('-');
    },
    isLoading() {
      return SIDE_KEYS.some((side) => this.tracks[side].loading);
    },
    legendItems() {
      return SIDE_KEYS.map((side) => ({
        side,
        color: SIDE_COLORS[side],
        label: this.voyages[side].mvygName || `Voyage ${side.toUpperCase()}`,
        note: STATUS_NOTE[this.tracks[side].status] || '',
      }));
    },
  },
  watch: {
    voyageKey() {
      this.loadTracks();
    },
  },
  created() {
    // titik trail gede, sengaja gak reaktif
    this.points = { a: [], b: [] };
    this.hoverMarkers = {};
    this.requestToken = 0;
  },
  async mounted() {
    // css leaflet cuma dimuat komponen peta, halaman ini belum tentu pernah buka peta
    await ensureStylesheet('/style/leaflet.css');
    if (this._isDestroyed) return;
    const { L } = window;
    this.map = L.map(this.$refs.map).setView([-2.5, 112], 5);
    L.tileLayer('https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}&gl=id&hl=id', {
      maxZoom: 19,
      attribution: 'Map data © Google Maps',
    }).addTo(this.map);
    this.layers = L.layerGroup().addTo(this.map);
    this.offHover = subscribe(`${HOVER}:${this.syncGroup}`, this.onHover);
    this.resizeObserver = new ResizeObserver(() => {
      if (this.map) this.map.invalidateSize();
    });
    this.resizeObserver.observe(this.$refs.map);
    this.loadTracks();
  },
  beforeDestroy() {
    if (this.offHover) this.offHover();
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.map) this.map.remove();
    this.map = null;
  },
  methods: {
    startOf(side) {
      return apiDatetimeToUnix(this.voyages[side].mvygStartTime);
    },
    async loadTracks() {
      this.requestToken += 1;
      const token = this.requestToken;
      await Promise.all(SIDE_KEYS.map(async (side) => {
        const track = this.tracks[side];
        const voyage = this.voyages[side];
        track.loading = true;
        track.status = '';
        this.points[side] = [];
        try {
          const response = await SummarySvc.getTrail({
            devcMassId: voyage.mvygMassId,
            start: this.startOf(side),
            end: apiDatetimeToUnix(voyage.mvygEndTime),
          });
          if (token !== this.requestToken) return;
          this.points[side] = parseTrailResponse(response?.data?.data).coordinatesData;
          track.status = this.points[side].length ? 'ok' : 'empty';
        } catch (error) {
          if (token !== this.requestToken) return;
          if (!axios.isCancel(error)) console.error('VoyageCompareMap trail error:', error?.message);
          track.status = 'failed';
        } finally {
          if (token === this.requestToken) track.loading = false;
        }
      }));
      if (token === this.requestToken && this.map) this.drawTracks();
    },
    drawTracks() {
      const { L } = window;
      this.layers.clearLayers();
      this.hoverMarkers = {};
      const bounds = [];
      SIDE_KEYS.forEach((side) => {
        const color = SIDE_COLORS[side];
        const voyage = this.voyages[side];
        const latLngs = this.points[side].map((point) => point.latLong);
        if (latLngs.length) {
          L.polyline(latLngs, { color, weight: 4, opacity: 0.85 }).addTo(this.layers);
          bounds.push(...latLngs);
        }
        routeMarkerPoints(voyage.mvygOriginLndm, voyage.mvygDestinationLndm).forEach((point) => {
          const isOrigin = point.key === 'A';
          L.circleMarker([point.lat, point.lng], {
            radius: 7,
            color,
            weight: 3,
            fillColor: isOrigin ? color : '#fff',
            fillOpacity: 1,
          })
            .bindTooltip(`${side.toUpperCase()} · ${isOrigin ? 'From' : 'To'} ${escapeHtml(point.name)}`)
            .addTo(this.layers);
          bounds.push([point.lat, point.lng]);
        });
        this.hoverMarkers[side] = L.circleMarker([0, 0], {
          radius: 8,
          color: '#fff',
          weight: 3,
          fillColor: color,
          fillOpacity: 1,
          interactive: false,
        });
      });
      if (bounds.length) this.map.fitBounds(L.latLngBounds(bounds), { padding: [30, 30], maxZoom: 12 });
    },
    // hover chart (ms sejak mulai) -> titik trail masing-masing voyage
    onHover(ms) {
      if (!this.map) return;
      SIDE_KEYS.forEach((side) => {
        const marker = this.hoverMarkers[side];
        if (!marker) return;
        const index = ms === null ? -1 : nearestIndexByElapsed(this.points[side], this.startOf(side), ms);
        if (index < 0) {
          this.layers.removeLayer(marker);
          return;
        }
        marker.setLatLng(this.points[side][index].latLong);
        if (!this.layers.hasLayer(marker)) marker.addTo(this.layers);
      });
    },
  },
};
</script>

<style scoped>
.voyage-compare-map {
  position: relative;
  height: 420px;
  border: 1px solid var(--mz-border);
  border-radius: 8px;
  overflow: hidden;
}

.voyage-compare-map-canvas {
  width: 100%;
  height: 100%;
}

.voyage-compare-map-legend {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-width: calc(100% - 80px);
  padding: 6px 10px;
  border-radius: 6px;
  background: var(--mz-map-chip-bg);
  font-size: var(--mz-fs-sm);
  color: var(--mz-text);
  pointer-events: none;
}

.voyage-compare-map-legend-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.voyage-compare-map-legend-line {
  flex: none;
  width: 18px;
  height: 4px;
  border-radius: 2px;
}

.voyage-compare-map-legend-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.voyage-compare-map-legend-note {
  flex: none;
  color: #b45309;
}

.voyage-compare-map-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  transform: translate(-50%, -50%);
  border: 1px solid var(--mz-border);
  border-radius: 16px;
  background: var(--mz-map-chip-bg);
  font-size: var(--mz-fs-sm);
  color: var(--mz-text);
  pointer-events: none;
}

@media (max-width: 767.98px) {
  .voyage-compare-map {
    height: 320px;
  }
}
</style>
